import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'

import { listRequests } from '../../api/portal'
import type { ApiRequestRow } from '../../api/portal'
import { usePortal } from '../../auth/sessionContext'
import { DataState } from '../../components/DataState'
import { EmptyState } from '../../components/ui/EmptyState'
import { Fact } from '../../components/ui/Fact'
import { Ltr } from '../../components/ui/Ltr'
import { PageHeader } from '../../components/ui/PageHeader'
import { Pager } from '../../components/ui/Pager'
import { SectionCard } from '../../components/ui/SectionCard'
import { StatusChip } from '../../components/ui/StatusChip'
import { CopyableId } from '../../components/ui/CopyableId'
import { RequestDetail } from '../requests/RequestDetail'
import { requestMoney } from '../requests/money'
import { formatCount, formatDateTime, formatIrr } from '../../format'

const RANGES = [
  { value: 'today', label: 'امروز', days: 0 },
  { value: 'month', label: 'این ماه', days: 30 },
  { value: 'all', label: 'همهٔ زمان‌ها', days: null },
] as const

const STATUSES = [
  { value: '', label: 'همهٔ نتیجه‌ها' },
  { value: 'succeeded', label: 'موفق' },
  { value: 'failed', label: 'ناموفق' },
]

function since(days: number | null): number {
  if (days === null) return 0
  const start = new Date()
  if (days === 0) start.setHours(0, 0, 0, 0)
  else start.setDate(start.getDate() - days)
  return start.getTime()
}

/**
 * What the account spent, in a business's vocabulary.
 *
 * The same rows the developer portal calls «درخواست‌ها», asked a different
 * question. A development shop wants to know which call failed and what its
 * tracking id was; a business wants to know what today cost, what this month
 * cost, and what the money went on. So the totals come first and the tracking
 * id is a copyable detail rather than a column heading.
 *
 * Filtering by date is client-side over the current page on purpose. The
 * server paginates by recency, and adding a date parameter to the customer
 * request API for a summary card would be a contract change for a
 * presentation decision. It is stated on screen rather than implied.
 */
export function UsagePage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const [page, setPage] = useState(1)
  const [range, setRange] = useState<(typeof RANGES)[number]['value']>('month')
  const [status, setStatus] = useState('')
  const [service, setService] = useState('')
  const [selected, setSelected] = useState<ApiRequestRow | null>(null)

  const query = useQuery({
    queryKey: ['client', 'requests', customerId, page, status, ''],
    queryFn: ({ signal }) => listRequests(customerId, page, status ? { status } : {}, signal),
  })

  const all = useMemo(() => query.data?.results ?? [], [query.data])
  const cutoff = since(RANGES.find((r) => r.value === range)?.days ?? null)
  const rows = useMemo(
    () =>
      all.filter((row) => {
        if (service && row.operation_code !== service) return false
        return new Date(row.created_at).getTime() >= cutoff
      }),
    [all, cutoff, service],
  )

  const services = useMemo(() => {
    const seen = new Map<string, string>()
    for (const row of all) {
      seen.set(row.operation_code, row.operation_display_name_fa || row.operation_code)
    }
    return [...seen.entries()]
  }, [all])

  const totals = useMemo(() => {
    let charged = 0
    let refunded = 0
    for (const row of rows) {
      const money = requestMoney(row)
      charged += money.chargedIrr
      refunded += money.refundedIrr
    }
    return { charged, refunded, net: charged - refunded, count: rows.length }
  }, [rows])

  return (
    <Box>
      <PageHeader
        title="مصرف و هزینه‌ها"
        description="هر استعلامی که نرم‌افزار شما انجام داده، نتیجهٔ آن و هزینه‌ای که داشته است."
      />

      <Stack spacing={3}>
        <SectionCard>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact label="هزینهٔ خالص" hint={RANGES.find((r) => r.value === range)?.label}>
                {formatIrr(totals.net)}
              </Fact>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact label="تعداد استعلام‌ها">{formatCount(totals.count)}</Fact>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact label="مبلغ کسرشده">{formatIrr(totals.charged)}</Fact>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact label="مبلغ بازگشتی">
                {totals.refunded > 0 ? formatIrr(totals.refunded) : '—'}
              </Fact>
            </Grid>
          </Grid>
        </SectionCard>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
          <TextField
            select
            size="small"
            label="بازهٔ زمانی"
            value={range}
            onChange={(event) => setRange(event.target.value as typeof range)}
            sx={{ minWidth: 160 }}
          >
            {RANGES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="نتیجه"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value)
              setPage(1)
            }}
            sx={{ minWidth: 160 }}
          >
            {STATUSES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="سرویس"
            value={service}
            onChange={(event) => setService(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">همهٔ سرویس‌ها</MenuItem>
            {services.map(([code, name]) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <SectionCard title="ریز مصرف" flush>
          <DataState
            isPending={query.isPending}
            isError={query.isError}
            error={query.error}
            onRetry={() => void query.refetch()}
          >
            {rows.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <EmptyState
                  title={
                    all.length === 0
                      ? 'هنوز استعلامی ثبت نشده است'
                      : 'در این بازه استعلامی ثبت نشده است'
                  }
                  hint={
                    all.length === 0
                      ? 'به محض نخستین استعلام، هزینه و نتیجهٔ آن اینجا دیده می‌شود.'
                      : 'بازهٔ دیگری را انتخاب کنید.'
                  }
                />
              </Box>
            ) : (
              // Rows, not a table. A business reads these on a phone, and a
              // ten-column financial table compressed to 390px is unreadable.
              <Stack divider={<Divider />}>
                {rows.map((row) => {
                  const money = requestMoney(row)
                  return (
                    <Box
                      key={row.id}
                      onClick={() => setSelected(row)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ alignItems: 'center', minWidth: 0 }}
                      >
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {row.operation_display_name_fa || row.operation_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(row.created_at)}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                          <StatusChip status={row.status} />
                        </Box>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 600,
                            flexShrink: 0,
                            minWidth: 96,
                            textAlign: 'end',
                          }}
                        >
                          {money.netIrr === 0 ? 'بدون هزینه' : formatIrr(-money.netIrr)}
                        </Typography>
                      </Stack>
                      {money.refundedIrr > 0 ? (
                        <Typography variant="caption" color="success.main">
                          {formatIrr(money.refundedIrr)} بازگشت داده شد
                        </Typography>
                      ) : null}
                      {/* The tracking id, for the one conversation in a hundred
                          that needs it. Secondary by placement and by weight,
                          and copyable without opening anything. */}
                      <Box sx={{ mt: 0.5 }} onClick={(event) => event.stopPropagation()}>
                        <Ltr mono variant="caption" color="text.secondary">
                          <CopyableId value={row.request_id} visible={10} label="شناسهٔ پیگیری" />
                        </Ltr>
                      </Box>
                    </Box>
                  )
                })}
              </Stack>
            )}
            <Box sx={{ p: 2 }}>
              <Pager page={page} count={query.data?.count ?? 0} onChange={setPage} />
            </Box>
          </DataState>
        </SectionCard>
      </Stack>

      <RequestDetail request={selected} onClose={() => setSelected(null)} />
    </Box>
  )
}
